<template>
    <div v-cloak>
        <div class="row">
            <div class="col-md-12">
                <nav aria-label="breadcrumb">
                    <ol class="breadcrumb">
                        <li class="breadcrumb-item">
                            <router-link :to="{ name: 'home' }">{{ $t("inicio") }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="{ name: 'informes' }">{{ $t("informes") }}</router-link>
                        </li>
                        <li class="breadcrumb-item active" aria-current="page">{{ $t("cierreCajaZ") }}</li>
                    </ol>
                </nav>
            </div>

            <!-- Filtros de consulta (van al servidor) -->
            <div class="col-md-12">
                <div class="card cz-filtros">
                    <div class="cz-filtros-fila">
                        <div class="cz-filtro cz-filtro-empresa">
                            <label>{{ $t("empresa") }}</label>
                            <select class="form-control" v-model="idCompany" @change="onCompanyChange">
                                <option v-for="c in listCompanies" :key="c.id" :value="c.id">{{ c.name }}</option>
                            </select>
                        </div>

                        <!-- Sectorización: el sector sustituye al filtro de negocios (los TPV se acotan
                             después con el filtro de vista, sobre las Z ya cargadas) -->
                        <div v-if="modoSectores" class="cz-filtro cz-filtro-negocios">
                            <label>{{ $t("sector") }}</label>
                            <el-select v-model="idSector" filterable class="cz-select" :placeholder="$t('seleccionaSector')">
                                <el-option v-for="sc in sectoresEmpresa" :key="sc.id" :label="sc.name" :value="sc.id" />
                            </el-select>
                        </div>

                        <div v-else class="cz-filtro cz-filtro-negocios">
                            <label>{{ $t("negocios") }}</label>
                            <el-select v-model="idsBusiness" multiple collapse-tags clearable class="cz-select"
                                :placeholder="$t('todaLaEmpresa')">
                                <el-option v-for="b in listBusinesses" :key="b.id" :label="b.establishmentName"
                                    :value="b.id" />
                            </el-select>
                        </div>

                        <div class="cz-filtro cz-filtro-fecha">
                            <label>{{ rangoActivo ? $t("fechaInicio") : $t("fecha") }}</label>
                            <datetime value-zone="Europe/Madrid" :auto="true" class="theme-numier" format="dd-MM-yyyy"
                                :phrases="{ ok: $t('aceptar'), cancel: $t('salir') }" input-class="form-control"
                                v-model="datefrom" />
                        </div>

                        <div class="cz-filtro cz-filtro-fecha" v-if="rangoActivo">
                            <label>{{ $t("fechaFin") }}</label>
                            <datetime value-zone="Europe/Madrid" :auto="true" class="theme-numier" format="dd-MM-yyyy"
                                :phrases="{ ok: $t('aceptar'), cancel: $t('salir') }" input-class="form-control"
                                v-model="dateto" />
                        </div>

                        <div class="cz-filtro cz-filtro-estado">
                            <label>{{ $t("estadoZ") }}</label>
                            <select class="form-control" v-model="estadoZ">
                                <option value="todas">{{ $t("todas") }}</option>
                                <option value="cerradas">{{ $t("zCerradas") }}</option>
                                <option value="abiertas">{{ $t("zAbiertas") }}</option>
                            </select>
                        </div>

                        <div class="cz-filtro cz-filtro-accion">
                            <button class="btn btn-primary btn-fill" :disabled="cargando || (modoSectores && !idSector)" @click="consultar()">
                                <i class="fa fa-search"></i> {{ $t("consultar") }}
                            </button>
                        </div>
                    </div>

                    <div class="cz-opciones">
                        <label class="cz-check" :class="{ 'cz-check-deshabilitado': modoSectores }">
                            <input type="checkbox" v-model="rangoActivo" :disabled="modoSectores" /> {{ $t("consultarRangoFechas") }}
                        </label>
                        <label class="cz-check"><input type="checkbox" v-model="ocultarVacias" /> {{ $t("ocultarFilasSinDatos") }}</label>
                    </div>
                    <div v-if="modoSectores" class="cz-nota-sectores">
                        <i class="fas fa-info-circle"></i> {{ $t("rangoNoDisponibleSectores") }}
                    </div>
                </div>
            </div>

            <!-- Sin datos -->
            <div class="col-md-12" v-if="consultado && !tieneDatos">
                <div class="card cz-vacio">
                    <div class="cz-vacio-icono"><i class="fas fa-cash-register"></i></div>
                    <h4>{{ $t("sinSesionesDeCaja") }}</h4>
                    <p>{{ $t("sinSesionesDeCajaTexto") }}</p>
                    <div v-if="tpvsSinApertura.length" class="cz-vacio-tpvs">
                        <span class="cz-vacio-etiqueta">{{ $t("tpvsSinApertura") }}</span>
                        <span v-for="t in tpvsSinApertura" :key="'sa-' + t.idTpv" class="cz-chip-tpv">{{ t.nombreNegocio }} · {{ t.nombre }}</span>
                    </div>
                </div>
            </div>

            <template v-if="tieneDatos">
                <!-- Avisos: una línea de resumen; al pulsar se despliegan por tipo -->
                <div class="col-md-12">
                    <div class="cz-resumen-avisos" :class="{ 'cz-resumen-avisos-alerta': avisosGlobales.length > 0 }">
                        <div class="cz-resumen-avisos-cabecera" @click="avisosDesplegados = !avisosDesplegados">
                            <i class="fas" :class="avisosGlobales.length ? 'fa-exclamation-triangle' : 'fa-info-circle'"></i>
                            <span class="cz-resumen-avisos-texto">
                                <strong>{{ columnasFiltradas.length }}</strong> {{ $t("zEnElInforme") }}
                                <template v-if="avisosGlobales.length"> · <strong>{{ avisosGlobales.length }}</strong> {{ $t("avisos").toLowerCase() }}</template>
                                <template v-if="tpvsSinApertura.length"> · <strong>{{ tpvsSinApertura.length }}</strong> {{ $t("tpvsSinApertura").toLowerCase() }}</template>
                            </span>
                            <span class="cz-resumen-avisos-accion">
                                {{ avisosDesplegados ? $t("ocultarDetalle") : $t("verDetalle") }}
                                <i class="fas" :class="avisosDesplegados ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                            </span>
                        </div>
                        <div v-if="avisosDesplegados" class="cz-resumen-avisos-cuerpo">
                            <div v-for="grupo in avisosPorTipo" :key="'g-' + grupo.codigo" class="cz-aviso-grupo" :class="'cz-aviso-' + grupo.nivel">
                                <div class="cz-aviso-grupo-titulo">
                                    <i class="fas" :class="grupo.nivel === 'rojo' ? 'fa-exclamation-circle' : 'fa-exclamation-triangle'"></i>
                                    {{ $t(grupo.codigo) }} <span class="cz-contador">{{ grupo.items.length }}</span>
                                </div>
                                <ul>
                                    <li v-for="(a, i) in grupo.items" :key="'ga-' + grupo.codigo + '-' + i">
                                        {{ a.columna }}<template v-if="a.detalle"> — {{ a.detalle }}</template>
                                    </li>
                                </ul>
                            </div>
                            <div v-if="tpvsSinApertura.length" class="cz-aviso-grupo cz-aviso-gris">
                                <div class="cz-aviso-grupo-titulo"><i class="fas fa-power-off"></i> {{ $t("tpvsSinApertura") }} <span class="cz-contador">{{ tpvsSinApertura.length }}</span></div>
                                <ul><li v-for="t in tpvsSinApertura" :key="'sa2-' + t.idTpv">{{ t.nombreNegocio }} · {{ t.nombre }}</li></ul>
                            </div>
                            <div class="cz-aviso-grupo cz-aviso-gris">
                                <div class="cz-aviso-grupo-titulo"><i class="fas fa-qrcode"></i> {{ $t("facturasQRExcluidasTexto") }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Barra de vista: filtros que no van al servidor + exportación -->
                <div class="col-md-12">
                    <div class="card cz-barra">
                        <div class="cz-barra-fila">
                            <div class="cz-barra-grupo">
                                <label>{{ $t("tpv") }}</label>
                                <el-select v-model="tpvsVisibles" multiple collapse-tags clearable size="small" class="cz-select-barra"
                                    :placeholder="$t('todos')">
                                    <el-option v-for="t in tpvsDisponibles" :key="t.idTpv" :label="t.etiqueta" :value="t.idTpv" />
                                </el-select>
                            </div>
                            <div class="cz-barra-grupo">
                                <label>{{ $t("operario") }}</label>
                                <el-select v-model="operariosVisibles" multiple collapse-tags clearable size="small" class="cz-select-barra"
                                    :placeholder="$t('todos')">
                                    <el-option v-for="o in operariosDisponibles" :key="o" :label="o" :value="o" />
                                </el-select>
                            </div>
                            <div class="cz-barra-grupo cz-barra-grupo-franja">
                                <label>{{ $t("franjaHoraria") }}
                                    <i class="fas fa-info-circle cz-ayuda" v-tooltip.top-center="$i18n.t('franjaHorariaSoloAnalisis')"></i>
                                </label>
                                <div class="cz-franja">
                                    <input type="time" class="form-control input-sm" v-model="horaDesde" />
                                    <span>–</span>
                                    <input type="time" class="form-control input-sm" v-model="horaHasta" />
                                    <button class="btn btn-default btn-sm" @click="aplicarFranja()">{{ $t("aplicar") }}</button>
                                    <button v-if="filtroHorarioActivo" class="btn btn-link btn-sm cz-quitar" @click="quitarFranja()"
                                        :title="$t('quitarFranja')"><i class="fas fa-times"></i></button>
                                </div>
                            </div>
                            <div class="cz-barra-exportar">
                                <label>{{ $t("exportarInforme") }}</label>
                                <div class="cz-botones-exportar">
                                    <button class="btn btn-sm cz-btn-pdf" @click="exportar('PDF')" v-tooltip.top-center="$i18n.t('exportarPdf')">
                                        <i class="fas fa-file-pdf"></i> PDF
                                    </button>
                                    <button class="btn btn-sm cz-btn-excel" @click="exportar('Excel')" v-tooltip.top-center="$i18n.t('exportarExcel')">
                                        <i class="fas fa-file-excel"></i> Excel
                                    </button>
                                    <template v-if="analisisDisponible && analisisResuelto">
                                        <button v-if="analisisAccesible" class="btn btn-sm cz-btn-analisis" @click="abrirAnalisis()"
                                            v-tooltip.top-center="$i18n.t('verAnalisisNegocioAyuda')">
                                            <i class="fas fa-chart-line"></i> {{ $t("verAnalisisNegocio") }}
                                        </button>
                                        <!-- Informe aún en desarrollo y usuario sin developerView: deshabilitado, "Próximamente" al pasar el ratón -->
                                        <span v-else class="cz-btn-proximamente-wrap" v-tooltip.top-center="$i18n.t('proximamente')">
                                            <button type="button" class="btn btn-sm cz-btn-analisis cz-btn-proximamente" disabled>
                                                <i class="fas fa-chart-line"></i> {{ $t("verAnalisisNegocio") }}
                                            </button>
                                        </span>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Matriz -->
                <div class="col-md-12">
                    <div class="card cz-matriz-card">
                        <!-- Flechas flotantes: fijas a media altura de la pantalla mientras la matriz esté a la vista -->
                        <button v-show="flechasVisibles" type="button" class="cz-flecha" :style="{ left: flechaIzqLeft + 'px' }"
                            :disabled="matrizScroll <= 0" :title="$t('desplazarIzquierda')" @click="desplazarMatriz(-1)">
                            <i class="fas fa-chevron-left"></i>
                        </button>
                        <button v-show="flechasVisibles" type="button" class="cz-flecha" :style="{ left: flechaDerLeft + 'px' }"
                            :disabled="matrizAlFinal" :title="$t('desplazarDerecha')" @click="desplazarMatriz(1)">
                            <i class="fas fa-chevron-right"></i>
                        </button>
                        <div class="cz-scroll" ref="matrizScroll" @scroll="onMatrizScroll">
                            <table class="table table-bordered cz-matriz">
                                <thead>
                                    <tr class="cz-fila-negocio">
                                        <th class="cz-concepto"></th>
                                        <th v-for="g in gruposVista" :key="'neg-' + g.idBusiness" :colspan="g.colspan" class="text-center">
                                            {{ g.nombre }}
                                        </th>
                                        <th class="cz-total text-center">{{ $t("total") }}</th>
                                    </tr>
                                    <tr class="cz-fila-tpv">
                                        <th class="cz-concepto"></th>
                                        <th v-for="t in tpvsVista" :key="'tpv-' + t.idBusiness + '-' + t.idTpv" :colspan="t.colspan" class="text-center">
                                            {{ t.nombre }}
                                            <small v-if="t.columnas.length > 1" class="d-block text-muted">{{ t.columnas.length }} Z</small>
                                        </th>
                                        <th class="cz-total"></th>
                                    </tr>
                                    <tr class="cz-fila-z">
                                        <th class="cz-concepto">{{ $t("concepto") }}</th>
                                        <th v-for="c in columnasVista" :key="'z-' + c.clave" class="text-center"
                                            :class="{ 'cz-col-subtotal': c.tipo === 'subtotal', 'cz-col-abierta': c.estado === 'abierta' }">
                                            <template v-if="c.tipo === 'z'">
                                                <span class="cz-z">{{ etiquetaColumna(c) }}</span>
                                                <span v-if="c.estado === 'abierta'" class="cz-badge cz-badge-abierta">{{ $t("zAbierta") }}</span>
                                                <i v-if="c.avisos.length" class="fas fa-exclamation-triangle cz-icono-aviso"
                                                    v-tooltip.top-center="tooltipAvisos(c)"></i>
                                            </template>
                                            <template v-else>
                                                <span class="cz-subtotal-etiqueta">{{ $t("subtotal") }}</span>
                                            </template>
                                        </th>
                                        <th class="cz-total"></th>
                                    </tr>
                                </thead>

                                <tbody v-for="bloque in bloques" :key="bloque.clave" class="cz-bloque">
                                    <tr class="cz-fila-bloque" @click="alternarBloque(bloque.clave)">
                                        <td :colspan="columnasVista.length + 2">
                                            <i class="fas" :class="bloqueAbierto(bloque.clave) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                            {{ $t(bloque.etiqueta) }}
                                        </td>
                                    </tr>
                                    <template v-if="bloqueAbierto(bloque.clave)">
                                        <tr v-for="fila in filasVisibles(bloque)" :key="bloque.clave + '-' + fila.clave"
                                            :class="{ 'cz-destacada': fila.destacado, 'cz-nivel-2': fila.nivel === 2, 'cz-nivel-3': fila.nivel >= 3 }">
                                            <td class="cz-concepto">
                                                {{ etiquetaFila(fila) }}
                                                <span v-if="fila.specialType" class="cz-badge cz-badge-especial">{{ etiquetaSpecial(fila.specialType) }}</span>
                                            </td>
                                            <td v-for="c in columnasVista" :key="fila.clave + '-' + c.clave"
                                                :class="[claseCelda(fila), { 'cz-col-subtotal': c.tipo === 'subtotal', 'cz-negativo': esNegativo(valorCelda(fila, c)) }]">
                                                <a v-if="esFilaIncidencias(fila) && c.tipo === 'z' && valorCelda(fila, c) > 0" href="javascript:void(0)"
                                                    class="cz-enlace-incidencias" :title="$t('verIncidencias')" @click="verIncidencias(c, fila)">
                                                    {{ formatear(valorCelda(fila, c), fila.tipo) }} <i class="fas fa-external-link-alt"></i>
                                                </a>
                                                <template v-else>
                                                    {{ formatear(valorCelda(fila, c), fila.tipo) }}
                                                    <span v-if="porcentajeCelda(bloque, fila, c) !== null" class="cz-pct">{{ formatearPorcentaje(porcentajeCelda(bloque, fila, c)) }}</span>
                                                </template>
                                            </td>
                                            <td class="cz-total" :class="[claseCelda(fila), { 'cz-negativo': esNegativo(totalFila(fila)) }]">
                                                {{ esNumerica(fila) ? formatear(totalFila(fila), fila.tipo) : '' }}
                                                <span v-if="porcentajeTotal(bloque, fila) !== null" class="cz-pct">{{ formatearPorcentaje(porcentajeTotal(bloque, fila)) }}</span>
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Productos vendidos -->
                <div class="col-md-12">
                    <div class="card cz-seccion">
                        <div class="cz-seccion-cabecera" @click="alternarSeccion('productos')">
                            <h5><i class="fas" :class="seccionesAbiertas.productos ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                {{ $t("productosVendidos") }}
                                <small class="text-muted">{{ $t("productosVendidosTexto") }}</small>
                            </h5>
                            <span v-if="filtroHorarioActivo" class="cz-badge cz-badge-franja">{{ $t("franjaHoraria") }} {{ horaDesdeAplicada || '00:00' }}–{{ horaHastaAplicada || '23:59' }}</span>
                        </div>
                        <div v-if="seccionesAbiertas.productos" class="cz-seccion-cuerpo">
                            <div v-if="cargandoProductos" class="text-center text-muted"><i class="fa fa-spinner fa-spin"></i> {{ $t("cargando") }}</div>
                            <template v-else>
                                <input type="text" class="form-control cz-buscador" v-model="buscarProducto" :placeholder="$t('buscarProducto')" />
                                <div class="cz-scroll">
                                    <table class="table table-bordered table-hover cz-tabla-productos">
                                        <thead>
                                            <tr>
                                                <th @click="ordenarProductos('nombre')" class="cz-ordenable">{{ $t("producto") }}</th>
                                                <th v-for="c in columnasZVista" :key="'p-' + c.clave" class="text-right">{{ nombreColumna(c.clave) }}</th>
                                                <th @click="ordenarProductos('totalUnidades')" class="text-right cz-ordenable">{{ $t("unidades") }}</th>
                                                <th @click="ordenarProductos('totalImporte')" class="text-right cz-ordenable">{{ $t("importe") }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="p in productosVista" :key="p.idProduct + '-' + p.esMenu">
                                                <td>{{ p.nombre }} <span v-if="p.esMenu" class="cz-badge cz-badge-menu">{{ $t("menu") }}</span></td>
                                                <td v-for="c in columnasZVista" :key="'pv-' + p.idProduct + '-' + c.clave" class="text-right">
                                                    {{ p.valores[c.clave] ? formatearUnidades(p.valores[c.clave].unidades) : '' }}
                                                </td>
                                                <td class="text-right"><strong>{{ formatearUnidades(unidadesVisibles(p)) }}</strong></td>
                                                <td class="text-right">{{ formatear(importeVisible(p), 'importe') }}</td>
                                            </tr>
                                            <tr v-if="!productosVista.length"><td :colspan="columnasZVista.length + 3" class="text-muted">{{ $t("sinDatos") }}</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Incidencias -->
                <div class="col-md-12" ref="seccionIncidencias">
                    <div class="card cz-seccion">
                        <div class="cz-seccion-cabecera" @click="alternarSeccion('incidencias')">
                            <h5><i class="fas" :class="seccionesAbiertas.incidencias ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                {{ $t("incidencias") }}
                                <small v-if="totalIncidenciasVisibles" class="text-muted">({{ totalIncidenciasVisibles }})</small>
                                <small class="text-muted">{{ $t("incidenciasTexto") }}</small>
                            </h5>
                        </div>
                        <div v-if="seccionesAbiertas.incidencias" class="cz-seccion-cuerpo">
                            <div v-if="cargandoIncidencias" class="text-center text-muted"><i class="fa fa-spinner fa-spin"></i> {{ $t("cargando") }}</div>
                            <template v-else>
                                <div class="cz-tickets-filtros">
                                    <select class="form-control" v-model="incidenciasTipo">
                                        <option value="">{{ $t("todasLasIncidencias") }}</option>
                                        <option v-for="tipo in tiposIncidenciaDisponibles" :key="tipo" :value="tipo">{{ $t(tipo) }} ({{ incidenciasPorTipo[tipo] }})</option>
                                    </select>
                                    <select class="form-control" v-model="incidenciasColumna">
                                        <option value="">{{ $t("todasLasZ") }}</option>
                                        <option v-for="c in columnasZVista" :key="'ic-' + c.clave" :value="c.clave">{{ nombreColumna(c.clave) }}</option>
                                    </select>
                                </div>
                                <div class="cz-scroll">
                                    <table class="table table-bordered table-hover cz-tabla-tickets">
                                        <thead>
                                            <tr>
                                                <th>{{ $t("caja") }}</th>
                                                <th>{{ $t("hora") }}</th>
                                                <th>{{ $t("documento") }}</th>
                                                <th>{{ $t("operario") }}</th>
                                                <th>{{ $t("tipo") }}</th>
                                                <th>{{ $t("detalle") }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(inc, i) in incidenciasVista" :key="'inc-' + i" :class="{ 'cz-fila-rectificativa': inc.estadoDocumento === 'N' }">
                                                <td>{{ nombreColumna(inc.columna) }}</td>
                                                <td>{{ formatear(inc.hora, 'hora') }}</td>
                                                <td>
                                                    {{ inc.docNumber || ('#' + inc.idHeader) }}
                                                    <span v-if="inc.estadoDocumento === 'N'" class="cz-badge cz-badge-especial">{{ $t("anulado") }}</span>
                                                </td>
                                                <td>{{ inc.operario }}</td>
                                                <td><span class="cz-badge-doc" :class="'cz-badge-inc-' + inc.tipo">{{ $t(inc.tipo) }}</span></td>
                                                <td class="cz-detalle-incidencia">{{ inc.detalle }}</td>
                                            </tr>
                                            <tr v-if="!incidenciasVista.length"><td colspan="6" class="text-muted">{{ $t("sinIncidencias") }}</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Documentos -->
                <div class="col-md-12">
                    <div class="card cz-seccion">
                        <div class="cz-seccion-cabecera" @click="alternarSeccion('tickets')">
                            <h5><i class="fas" :class="seccionesAbiertas.tickets ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                {{ $t("documentos") }}
                                <small v-if="ticketsTotal" class="text-muted">({{ ticketsTotal }})</small>
                            </h5>
                            <span v-if="filtroHorarioActivo" class="cz-badge cz-badge-franja">{{ $t("franjaHoraria") }} {{ horaDesdeAplicada || '00:00' }}–{{ horaHastaAplicada || '23:59' }}</span>
                        </div>
                        <div v-if="seccionesAbiertas.tickets" class="cz-seccion-cuerpo">
                            <div class="cz-tickets-filtros">
                                <div class="cz-buscador-doc">
                                    <input type="text" class="form-control" v-model="buscarDocumento" :placeholder="$t('buscarDocumento')"
                                        @input="buscarDocumentoDiferido()" @keyup.enter="cargarTickets(1)" />
                                    <button v-if="buscarDocumento" type="button" class="cz-limpiar" :title="$t('limpiar')" @click="limpiarBusquedaDocumento()">
                                        <i class="fas fa-times"></i>
                                    </button>
                                </div>
                                <select class="form-control" v-model="ticketsTipoDocumento" @change="cargarTickets(1)">
                                    <option value="">{{ $t("todosLosDocumentos") }}</option>
                                    <option value="T">{{ $t("ticket") }}</option>
                                    <option value="F">{{ $t("factura") }}</option>
                                    <option value="R">{{ $t("rectificativa") }}</option>
                                </select>
                                <select class="form-control" v-model="ticketsPorPagina" @change="cargarTickets(1)">
                                    <option :value="25">25</option>
                                    <option :value="50">50</option>
                                    <option :value="100">100</option>
                                </select>
                            </div>
                            <div v-if="cargandoTickets" class="text-center text-muted"><i class="fa fa-spinner fa-spin"></i> {{ $t("cargando") }}</div>
                            <div v-else class="cz-scroll">
                                <table class="table table-bordered table-hover cz-tabla-tickets">
                                    <thead>
                                        <tr>
                                            <th style="width: 34px;"></th>
                                            <th>{{ $t("hora") }}</th>
                                            <th>{{ $t("tpv") }} / Z</th>
                                            <th>{{ $t("documento") }}</th>
                                            <th>{{ $t("operario") }}</th>
                                            <th>{{ $t("cliente") }}</th>
                                            <th class="text-center">{{ $t("nDeComensales") }}</th>
                                            <th>{{ $t("formasPago") }}</th>
                                            <th class="text-right">{{ $t("importe") }}</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="t in tickets" :key="'t-' + t.id">
                                        <tr :class="{ 'cz-fila-rectificativa': t.esRectificativa }">
                                            <td class="text-center">
                                                <button type="button" class="cz-btn-desplegar" @click="alternarTicket(t.id)">
                                                    <i class="fas" :class="ticketAbierto(t.id) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                                </button>
                                            </td>
                                            <td>{{ t.hora }}</td>
                                            <td>{{ nombreColumna(t.columna) }}</td>
                                            <td>
                                                <span class="cz-badge-doc" :class="'cz-badge-doc-' + t.tipoDocumento">{{ etiquetaTipoDocumento(t.tipoDocumento) }}</span>
                                                {{ t.docNumber }}
                                                <small v-if="t.esRectificativa && t.ref_docNumber" class="d-block text-muted">{{ $t("rectificaA") }} {{ t.ref_docNumber }}</small>
                                                <span v-if="t.esInvitacion" class="cz-badge cz-badge-especial">{{ $t("invitacion") }}</span>
                                            </td>
                                            <td>{{ t.workerName || '-' }}</td>
                                            <td>
                                                <template v-if="t.cliente">{{ t.cliente.nombre }}<small v-if="t.cliente.documento" class="d-block text-muted">{{ t.cliente.documento }}</small></template>
                                                <span v-else>-</span>
                                            </td>
                                            <td class="text-center">{{ t.comensales }}</td>
                                            <td>
                                                <span v-for="(p, i) in t.pagos" :key="'pg-' + t.id + '-' + i" class="cz-chip-pago" :class="{ 'cz-chip-especial': p.specialType }">
                                                    {{ p.nombre }} <strong>{{ formatear(p.importe, 'importe') }}</strong>
                                                </span>
                                            </td>
                                            <td class="text-right" :class="{ 'cz-negativo': t.importe < 0 }"><strong>{{ formatear(t.importe, 'importe') }}</strong></td>
                                        </tr>
                                        <tr v-if="ticketAbierto(t.id)" class="cz-fila-lineas">
                                            <td></td>
                                            <td colspan="8">
                                                <table class="table table-sm cz-tabla-lineas">
                                                    <thead>
                                                        <tr>
                                                            <th class="text-right">{{ $t("unidades") }}</th>
                                                            <th>{{ $t("producto") }}</th>
                                                            <th class="text-right">{{ $t("precio") }}</th>
                                                            <th class="text-right">{{ $t("iva") }}</th>
                                                            <th class="text-right">{{ $t("importe") }}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="l in t.lineas" :key="'l-' + l.id" :class="{ 'cz-linea-menu': l.esMenu }">
                                                            <td class="text-right">{{ formatearUnidades(l.unidades) }}</td>
                                                            <td>
                                                                {{ l.nombre }}
                                                                <small v-if="l.complementos" class="d-block text-muted">{{ l.complementos }}</small>
                                                                <small v-if="l.textoAlternativo" class="d-block text-muted">{{ l.textoAlternativo }}</small>
                                                            </td>
                                                            <td class="text-right">{{ formatear(l.precio, 'importe') }}</td>
                                                            <td class="text-right">{{ l.iva }} %</td>
                                                            <td class="text-right">{{ formatear(l.importe, 'importe') }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="cz-paginacion" v-if="ticketsPaginas > 1">
                                <button class="btn btn-default btn-sm" :disabled="ticketsPagina <= 1" @click="cargarTickets(ticketsPagina - 1)">&laquo;</button>
                                <span>{{ ticketsPagina }} / {{ ticketsPaginas }}</span>
                                <button class="btn btn-default btn-sm" :disabled="ticketsPagina >= ticketsPaginas" @click="cargarTickets(ticketsPagina + 1)">&raquo;</button>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { Datetime } from "vue-datetime";
import { Select, Option } from "element-ui";
import moment from "moment";
import swal from "sweetalert2";
import { mapState, mapMutations } from "vuex";

/* Filas que son medias: no se suman ni en subtotales ni en el total filtrado. */
const FILAS_MEDIA = ["ticketMedio", "mediaPorComensal", "duracionHoras"];

/* Cierre a desarrolladores de "Análisis de negocio": con true solo lo abren los usuarios con la
   config 133 "developerView" (cargada en el store al entrar en el dashboard) y al resto el botón
   sale deshabilitado con "Próximamente". Publicado: false (misma bandera que en Informes.vue). */
const ANALISIS_SOLO_DESARROLLO = false;

export default {
    components: {
        Datetime,
        [Select.name]: Select,
        [Option.name]: Option,
    },

    data() {
        return {
            // Filtros de consulta
            listCompanies: [],
            idCompany: null,
            listBusinesses: [],
            idsBusiness: [],
            // Sectorización (usuarios con sectores): el sector sustituye a los negocios
            idSector: null,
            datefrom: moment().format("YYYY-MM-DD"),
            dateto: moment().format("YYYY-MM-DD"),
            rangoActivo: false,
            estadoZ: "todas",
            // Enlace a "Análisis de negocio": se oculta si el usuario tiene la restricción 34
            // (y solo enlaza si el informe está publicado o el usuario tiene developerView)
            analisisDisponible: true,
            ocultarVacias: true,

            // Resultado
            cargando: false,
            consultado: false,
            columnas: [],
            grupos: [],
            bloques: [],
            tpvsSinApertura: [],
            bloquesCerrados: {},
            avisosDesplegados: false,
            sidebarMinimizadoAqui: false,
            seccionPendiente: "",

            // Filtros de vista
            tpvsVisibles: [],
            operariosVisibles: [],
            horaDesde: "",
            horaHasta: "",
            horaDesdeAplicada: "",
            horaHastaAplicada: "",

            // Secciones bajo demanda
            seccionesAbiertas: { productos: false, incidencias: false, tickets: false },
            incidencias: [],
            incidenciasPorTipo: {},
            cargandoIncidencias: false,
            incidenciasTipo: "",
            incidenciasColumna: "",
            // Desplazamiento horizontal de la matriz (flechas flotantes)
            matrizDesborda: false,
            matrizScroll: 0,
            matrizAlFinal: false,
            flechasVisibles: false,
            flechaIzqLeft: 0,
            flechaDerLeft: 0,
            productos: [],
            cargandoProductos: false,
            buscarProducto: "",
            ordenProductos: { campo: "nombre", asc: true },
            tickets: [],
            ticketsTotal: 0,
            ticketsPagina: 1,
            ticketsPorPagina: 50,
            cargandoTickets: false,
            ticketsTipoDocumento: "",
            ticketsAbiertos: {},
            buscarDocumento: "",
            buscarDocumentoTimer: null,
        };
    },

    created() {
        // Desde Análisis de negocio se llega con ?fecha=AAAA-MM-DD (&idCompany, &idsBusiness o
        // &idSector, &seccion=incidencias): se consulta ese día directamente.
        this.getCompanies().then(() => this.aplicarQuery());
        this.comprobarAnalisisDisponible();
        if (this.modoSectores) {
            this.rangoActivo = false;
            this.initSectores();
        }
        window.addEventListener("resize", this.medirMatriz);
        window.addEventListener("scroll", this.posicionarFlechas, { passive: true });
    },

    /* La matriz es ancha: al entrar se pliega el menú lateral para ganar sitio
       y al salir se deja como estaba. */
    mounted() {
        if (this.$sidebar && !this.$sidebar.isMinimized) {
            this.$sidebar.toggleMinimize();
            this.sidebarMinimizadoAqui = true;
        }
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.medirMatriz);
        window.removeEventListener("scroll", this.posicionarFlechas);
        if (this.sidebarMinimizadoAqui && this.$sidebar && this.$sidebar.isMinimized) {
            this.$sidebar.toggleMinimize();
        }
    },

    watch: {
        columnasVista() {
            this.$nextTick(this.medirMatriz);
        },
        sectoresEmpresa(lista) {
            if (!this.modoSectores) return;
            if (!lista.some(sc => sc.id === this.idSector)) {
                this.idSector = lista.length ? lista[0].id : null;
            }
        },
    },

    computed: {
        analisisAccesible() {
            return !ANALISIS_SOLO_DESARROLLO || this.$store.state.developerView === true;
        },
        /* Hasta que el store sepa si hay developerView no se pinta el botón (evita el parpadeo a "Próximamente") */
        analisisResuelto() {
            return !ANALISIS_SOLO_DESARROLLO || this.$store.state.developerView !== null;
        },
        ...mapState(["listaSectores", "sectores"]),

        /* Mismo criterio que Ventas por negocio: el usuario trabaja por sectores. */
        modoSectores() {
            return !!this.sectores && this.$cookies && this.$cookies.isKey("sectores");
        },

        /* Sectores de la empresa seleccionada; si el dato no trae empresa, todos. */
        sectoresEmpresa() {
            const lista = this.listaSectores || [];
            if (!this.idCompany) return lista;
            const propios = lista.filter(sc => sc.idCompany !== undefined && parseInt(sc.idCompany, 10) === parseInt(this.idCompany, 10));
            return propios.length ? propios : lista;
        },

        tieneDatos() {
            return this.columnas.length > 0;
        },

        tiposIncidenciaDisponibles() {
            return Object.keys(this.incidenciasPorTipo).filter(t => this.incidenciasPorTipo[t] > 0);
        },

        incidenciasVista() {
            return this.incidencias.filter(i => {
                if (this.incidenciasTipo && i.tipo !== this.incidenciasTipo) return false;
                if (this.incidenciasColumna && i.columna !== this.incidenciasColumna) return false;
                if (this.filtroVistaActivo && !this.columnasFiltradas.some(c => c.clave === i.columna)) return false;
                return true;
            });
        },

        totalIncidenciasVisibles() {
            return this.columnasFiltradas.reduce((n, c) => {
                const aviso = (c.avisos || []).find(a => a.codigo === "avisoZConIncidencias");
                return n + (aviso ? Number(aviso.detalle) || 0 : 0);
            }, 0);
        },

        filtroVistaActivo() {
            return this.tpvsVisibles.length > 0 || this.operariosVisibles.length > 0;
        },

        columnasFiltradas() {
            return this.columnas.filter(c => {
                if (this.tpvsVisibles.length && !this.tpvsVisibles.includes(c.idTpv)) return false;
                if (this.operariosVisibles.length && !this.operariosVisibles.includes(c.operario || "")) return false;
                return true;
            });
        },

        /* TPVs de la cabecera en una sola lista (los grupos por negocio, aplanados): en Vue 2
           <template v-for> no admite key y el editor lo marca. */
        tpvsVista() {
            const lista = [];
            this.gruposVista.forEach(g => {
                g.tpvs.forEach(t => lista.push(Object.assign({ idBusiness: g.idBusiness }, t)));
            });
            return lista;
        },

        /* Negocio → TPV → columnas, sobre las columnas visibles. Un TPV con varias
           Z lleva una columna de subtotal detrás de las suyas. */
        gruposVista() {
            const grupos = [];
            const indice = {};

            this.columnasFiltradas.forEach(c => {
                if (!indice[c.idBusiness]) {
                    indice[c.idBusiness] = { idBusiness: c.idBusiness, nombre: c.nombreNegocio, tpvs: [], tpvIndice: {}, colspan: 0 };
                    grupos.push(indice[c.idBusiness]);
                }
                const g = indice[c.idBusiness];
                if (!g.tpvIndice[c.idTpv]) {
                    g.tpvIndice[c.idTpv] = { idTpv: c.idTpv, nombre: c.nombreTpv, columnas: [], colspan: 0 };
                    g.tpvs.push(g.tpvIndice[c.idTpv]);
                }
                g.tpvIndice[c.idTpv].columnas.push(c);
            });

            grupos.forEach(g => {
                g.tpvs.forEach(t => {
                    t.colspan = t.columnas.length + (t.columnas.length > 1 ? 1 : 0);
                    g.colspan += t.colspan;
                });
            });

            return grupos;
        },

        columnasVista() {
            const lista = [];
            this.gruposVista.forEach(g => {
                g.tpvs.forEach(t => {
                    t.columnas.forEach(c => lista.push(Object.assign({ tipo: "z" }, c)));
                    if (t.columnas.length > 1) {
                        lista.push({
                            tipo: "subtotal",
                            clave: "sub-" + t.idTpv,
                            idTpv: t.idTpv,
                            nombreTpv: t.nombre,
                            columnas: t.columnas.map(c => c.clave),
                            avisos: [],
                            estado: "",
                        });
                    }
                });
            });
            return lista;
        },

        columnasZVista() {
            return this.columnasVista.filter(c => c.tipo === "z");
        },

        tpvsDisponibles() {
            const vistos = {};
            const lista = [];
            this.columnas.forEach(c => {
                if (!vistos[c.idTpv]) {
                    vistos[c.idTpv] = true;
                    lista.push({ idTpv: c.idTpv, etiqueta: c.nombreNegocio + " · " + c.nombreTpv });
                }
            });
            return lista;
        },

        operariosDisponibles() {
            const vistos = {};
            this.columnas.forEach(c => { if (c.operario) vistos[c.operario] = true; });
            return Object.keys(vistos).sort();
        },

        avisosGlobales() {
            const avisos = [];
            this.columnasFiltradas.forEach(c => {
                (c.avisos || []).forEach(a => {
                    avisos.push({
                        columna: c.nombreNegocio + " · " + c.nombreTpv + " · " + this.etiquetaColumna(c),
                        codigo: a.codigo,
                        detalle: a.detalle,
                        nivel: a.codigo === "avisoZAbierta" ? "rojo" : "ambar",
                    });
                });
            });
            return avisos;
        },

        /* Avisos agrupados por tipo (rojos primero) para el desplegable de resumen. */
        avisosPorTipo() {
            const grupos = [];
            const indice = {};
            this.avisosGlobales.forEach(a => {
                if (!indice[a.codigo]) {
                    indice[a.codigo] = { codigo: a.codigo, nivel: a.nivel, items: [] };
                    grupos.push(indice[a.codigo]);
                }
                indice[a.codigo].items.push({ columna: a.columna, detalle: a.detalle });
            });
            return grupos.sort((x, y) => (x.nivel === y.nivel ? 0 : (x.nivel === "rojo" ? -1 : 1)));
        },

        filtroHorarioActivo() {
            return this.horaDesdeAplicada !== "" || this.horaHastaAplicada !== "";
        },

        productosVista() {
            const texto = this.buscarProducto.trim().toLowerCase();
            let lista = this.productos.filter(p => !texto || (p.nombre || "").toLowerCase().includes(texto));
            // Con TPVs filtrados en la vista, un producto sin unidades en las Z visibles no se enseña.
            if (this.filtroVistaActivo) {
                lista = lista.filter(p => this.unidadesVisibles(p) !== 0);
            }
            const campo = this.ordenProductos.campo;
            const asc = this.ordenProductos.asc ? 1 : -1;
            return lista.slice().sort((a, b) => {
                const va = campo === "nombre" ? (a.nombre || "") : (campo === "totalUnidades" ? this.unidadesVisibles(a) : this.importeVisible(a));
                const vb = campo === "nombre" ? (b.nombre || "") : (campo === "totalUnidades" ? this.unidadesVisibles(b) : this.importeVisible(b));
                if (va < vb) return -1 * asc;
                if (va > vb) return 1 * asc;
                return 0;
            });
        },

        ticketsPaginas() {
            return Math.max(1, Math.ceil(this.ticketsTotal / this.ticketsPorPagina));
        },
    },

    methods: {
        /* ---------- Carga de selectores ---------- */

        getCompanies() {
            const config = {
                params: { jwt: this.$store.getters.jwt },
                headers: { Authorization: "Bearer " + this.$store.getters.jwt },
            };
            return this.axios.get(this.$store.getters.apiUrl + "general/getCompaniesByUser", config)
                .then(response => {
                    this.listCompanies = response.data.result || [];
                    if (this.listCompanies.length && !this.idCompany) {
                        this.idCompany = this.listCompanies[0].id;
                    }
                    return this.getBusinesses();
                })
                .catch(error => console.error(error));
        },

        getBusinesses() {
            if (!this.idCompany) return Promise.resolve();
            const config = {
                params: { idCompany: this.idCompany, jwt: this.$store.getters.jwt },
                headers: { Authorization: "Bearer " + this.$store.getters.jwt },
            };
            return this.axios.get(this.$store.getters.apiUrl + "businesses/getBusinessesByIdCompany", config)
                .then(response => {
                    this.listBusinesses = Array.isArray(response.data) ? response.data : (response.data.result || []);
                })
                .catch(error => console.error(error));
        },

        onCompanyChange() {
            this.idsBusiness = [];
            this.getBusinesses();
        },

        /* Parámetros de entrada desde otro informe (Análisis de negocio → día concreto). */
        aplicarQuery() {
            const q = (this.$route && this.$route.query) ? this.$route.query : {};
            if (!q.fecha || !/^\d{4}-\d{2}-\d{2}$/.test(String(q.fecha))) return;

            // Los ids se toman de las propias listas (y no de parseInt) para que el tipo
            // coincida con el value de las opciones del el-select: si no, el selector
            // muestra el id en crudo y al tocarlo se pierde la selección.
            let cadena = Promise.resolve();
            const empresa = q.idCompany ? this.listCompanies.find(c => String(c.id) === String(q.idCompany)) : null;
            if (empresa) {
                this.idCompany = empresa.id;
                cadena = this.getBusinesses() || Promise.resolve();
            }

            cadena.then(() => {
                if (q.idsBusiness) {
                    const pedidos = String(q.idsBusiness).split(",");
                    this.idsBusiness = this.listBusinesses.filter(b => pedidos.includes(String(b.id))).map(b => b.id);
                }
                if (q.idSector && this.modoSectores) {
                    this.idSector = parseInt(q.idSector, 10);
                }
                this.datefrom = String(q.fecha);
                this.dateto = String(q.fecha);
                this.rangoActivo = false;
                this.seccionPendiente = q.seccion ? String(q.seccion) : "";
                this.consultar();
            });
        },

        /* ---------- Sectorización ---------- */

        ...mapMutations(["changeListaSectores"]),

        initSectores() {
            if (this.listaSectores && this.listaSectores.length) {
                this.idSector = this.sectoresEmpresa.length ? this.sectoresEmpresa[0].id : this.listaSectores[0].id;
                return;
            }
            const config = {
                params: { jwt: this.$store.getters.jwt },
                headers: { Authorization: "Bearer " + this.$store.getters.jwt },
            };
            this.axios.get(this.$store.getters.apiUrl + "general/getBusinessesSectors", config)
                .then(response => {
                    if (!response.data.response) return;
                    const sectores = (response.data.result.sectores || []).map(sc => Object.assign({}, sc, {
                        id: parseInt(sc.id, 10),
                        idBusinessMaster: parseInt(sc.idBusinessMaster, 10),
                    }));
                    this.changeListaSectores(sectores);
                    if (sectores.length) {
                        this.idSector = this.sectoresEmpresa.length ? this.sectoresEmpresa[0].id : sectores[0].id;
                    }
                })
                .catch(error => console.error(error));
        },

        /* ---------- Consulta principal ---------- */

        baseParams() {
            const desde = moment(this.datefrom).startOf("day").unix();
            const hasta = this.rangoActivo && !this.modoSectores
                ? moment(this.dateto).endOf("day").unix()
                : moment(this.datefrom).endOf("day").unix();

            const params = {
                jwt: this.$store.getters.jwt,
                datefrom: desde,
                dateto: hasta,
                estadoZ: this.estadoZ,
            };
            params.idCompany = this.idCompany;
            if (this.modoSectores) {
                params.idSector = this.idSector;
            } else {
                params.idsBusiness = this.idsBusiness.join(",");
            }
            return params;
        },

        analisisParams() {
            return Object.assign(this.baseParams(), {
                horaDesde: this.horaDesdeAplicada,
                horaHasta: this.horaHastaAplicada,
            });
        },

        consultar() {
            if (!this.idCompany || (this.modoSectores && !this.idSector)) return;
            if (this.rangoActivo && moment(this.dateto).isBefore(moment(this.datefrom))) {
                swal({ title: this.$t("ups"), text: this.$t("laFechaDeInicioNoPuedeSerPosteriorALaDeFin"), type: "warning" });
                return;
            }

            this.cargando = true;
            this.consultado = false;
            this.seccionesAbiertas = { productos: false, incidencias: false, tickets: false };
            this.productos = [];
            this.incidencias = [];
            this.incidenciasPorTipo = {};
            this.incidenciasTipo = "";
            this.incidenciasColumna = "";
            this.tickets = [];
            this.ticketsTotal = 0;
            this.buscarDocumento = "";
            this.tpvsVisibles = [];
            this.operariosVisibles = [];
            this.avisosDesplegados = false;

            const config = {
                params: this.baseParams(),
                headers: { Authorization: "Bearer " + this.$store.getters.jwt },
            };

            this.axios.get(this.$store.getters.apiUrl + "cierrecaja/getCierreZ", config)
                .then(response => {
                    const datos = response.data || {};
                    if (!datos.response) {
                        this.columnas = []; this.grupos = []; this.bloques = []; this.tpvsSinApertura = [];
                        swal({ title: this.$t("atencion"), text: datos.message || this.$t("errorDesconocido"), type: "warning" });
                        return;
                    }
                    const r = datos.result;
                    this.columnas = r.columnas || [];
                    this.grupos = r.grupos || [];
                    this.bloques = r.bloques || [];
                    this.tpvsSinApertura = r.tpvsSinApertura || [];
                    this.$nextTick(this.medirMatriz);
                    if (this.seccionPendiente) {
                        const seccion = this.seccionPendiente;
                        this.seccionPendiente = "";
                        this.$nextTick(() => {
                            if (this.tieneDatos && this.seccionesAbiertas[seccion] === false) this.alternarSeccion(seccion);
                        });
                    }
                })
                .catch(error => {
                    console.error(error);
                    swal({ title: this.$t("error"), text: this.$t("errorDesconocido"), type: "error" });
                })
                .finally(() => {
                    this.cargando = false;
                    this.consultado = true;
                });
        },

        aplicarFranja() {
            this.horaDesdeAplicada = this.horaDesde;
            this.horaHastaAplicada = this.horaHasta;
            if (this.seccionesAbiertas.productos) this.cargarProductos();
            if (this.seccionesAbiertas.tickets) this.cargarTickets(1);
        },

        quitarFranja() {
            this.horaDesde = "";
            this.horaHasta = "";
            this.aplicarFranja();
        },

        /* ---------- Secciones bajo demanda ---------- */

        alternarSeccion(nombre) {
            this.$set(this.seccionesAbiertas, nombre, !this.seccionesAbiertas[nombre]);
            if (this.seccionesAbiertas[nombre]) {
                if (nombre === "productos" && !this.productos.length) this.cargarProductos();
                if (nombre === "incidencias" && !this.incidencias.length) this.cargarIncidencias();
                if (nombre === "tickets" && !this.tickets.length) this.cargarTickets(1);
            }
        },

        cargarIncidencias() {
            this.cargandoIncidencias = true;
            const config = { params: this.baseParams(), headers: { Authorization: "Bearer " + this.$store.getters.jwt } };
            this.axios.get(this.$store.getters.apiUrl + "cierrecaja/getCierreZIncidencias", config)
                .then(response => {
                    const r = (response.data && response.data.response) ? response.data.result : { incidencias: [], porTipo: {} };
                    this.incidencias = r.incidencias || [];
                    this.incidenciasPorTipo = r.porTipo || {};
                })
                .catch(error => { console.error(error); this.incidencias = []; this.incidenciasPorTipo = {}; })
                .finally(() => { this.cargandoIncidencias = false; });
        },

        esFilaIncidencias(fila) {
            return fila.clave === "numIncidencias" || fila.clave.indexOf("incidencias") === 0;
        },

        /* Desde la matriz: abre la sección de incidencias filtrada por esa Z (y por
           tipo si se pulsó en una fila de desglose) y baja hasta ella. */
        verIncidencias(columna, fila) {
            this.incidenciasColumna = columna.clave;
            this.incidenciasTipo = fila.clave === "numIncidencias" ? "" : fila.clave;
            if (!this.seccionesAbiertas.incidencias) {
                this.alternarSeccion("incidencias");
            }
            this.$nextTick(() => {
                const el = this.$refs.seccionIncidencias;
                if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        },

        /* ---------- Desplazamiento horizontal de la matriz ---------- */

        medirMatriz() {
            const el = this.$refs.matrizScroll;
            if (!el) { this.matrizDesborda = false; this.flechasVisibles = false; return; }
            this.matrizDesborda = el.scrollWidth > el.clientWidth + 2;
            this.onMatrizScroll();
            this.posicionarFlechas();
        },

        /* Las flechas son fijas a media altura de la ventana y se pegan a los bordes
           de la matriz; solo se ven mientras la matriz cruza el centro de la pantalla,
           que es justo cuando ni la cabecera ni la barra inferior están a mano. */
        posicionarFlechas() {
            const el = this.$refs.matrizScroll;
            if (!el || !this.matrizDesborda) { this.flechasVisibles = false; return; }
            const rect = el.getBoundingClientRect();
            const centro = window.innerHeight / 2;
            this.flechasVisibles = rect.top < centro - 24 && rect.bottom > centro + 24;
            this.flechaIzqLeft = Math.round(rect.left + 10);
            this.flechaDerLeft = Math.round(rect.right - 46);
        },

        onMatrizScroll() {
            const el = this.$refs.matrizScroll;
            if (!el) return;
            this.matrizScroll = el.scrollLeft;
            this.matrizAlFinal = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
        },

        desplazarMatriz(direccion) {
            const el = this.$refs.matrizScroll;
            if (!el) return;
            const paso = Math.max(200, Math.round(el.clientWidth * 0.6));
            if (el.scrollBy) {
                el.scrollBy({ left: direccion * paso, behavior: "smooth" });
            } else {
                el.scrollLeft += direccion * paso;
            }
        },

        cargarProductos() {
            this.cargandoProductos = true;
            const config = { params: this.analisisParams(), headers: { Authorization: "Bearer " + this.$store.getters.jwt } };
            this.axios.get(this.$store.getters.apiUrl + "cierrecaja/getCierreZProductos", config)
                .then(response => {
                    this.productos = (response.data && response.data.response) ? (response.data.result.productos || []) : [];
                })
                .catch(error => { console.error(error); this.productos = []; })
                .finally(() => { this.cargandoProductos = false; });
        },

        cargarTickets(pagina) {
            this.cargandoTickets = true;
            this.ticketsPagina = pagina;
            const config = {
                params: Object.assign(this.analisisParams(), {
                    pagina: pagina,
                    porPagina: this.ticketsPorPagina,
                    tipoDocumento: this.ticketsTipoDocumento,
                    textoDocumento: this.buscarDocumento.trim(),
                }),
                headers: { Authorization: "Bearer " + this.$store.getters.jwt },
            };
            this.axios.get(this.$store.getters.apiUrl + "cierrecaja/getCierreZTickets", config)
                .then(response => {
                    const r = (response.data && response.data.response) ? response.data.result : { total: 0, tickets: [] };
                    this.tickets = r.tickets || [];
                    this.ticketsTotal = r.total || 0;
                    this.ticketsAbiertos = {};
                })
                .catch(error => { console.error(error); this.tickets = []; this.ticketsTotal = 0; })
                .finally(() => { this.cargandoTickets = false; });
        },

        /* La búsqueda va al servidor (los documentos están paginados): se espera a
           que el usuario deje de escribir. La API entiende cualquier forma de
           escribir el número ("Nº FS 0101/0037335", "fs0101/37335", "37335"...). */
        buscarDocumentoDiferido() {
            clearTimeout(this.buscarDocumentoTimer);
            this.buscarDocumentoTimer = setTimeout(() => this.cargarTickets(1), 450);
        },

        limpiarBusquedaDocumento() {
            clearTimeout(this.buscarDocumentoTimer);
            this.buscarDocumento = "";
            this.cargarTickets(1);
        },

        alternarTicket(id) {
            this.$set(this.ticketsAbiertos, id, !this.ticketsAbiertos[id]);
        },

        ticketAbierto(id) {
            return !!this.ticketsAbiertos[id];
        },

        ordenarProductos(campo) {
            if (this.ordenProductos.campo === campo) {
                this.ordenProductos.asc = !this.ordenProductos.asc;
            } else {
                this.ordenProductos = { campo: campo, asc: campo === "nombre" };
            }
        },

        /* ---------- Exportación ---------- */

        exportar(tipo) {
            const params = this.analisisParams();
            if (this.tpvsVisibles.length) {
                params.idsTpv = this.tpvsVisibles.join(",");
            }
            const query = Object.keys(params)
                .filter(k => params[k] !== "" && params[k] !== null && params[k] !== undefined)
                .map(k => encodeURIComponent(k) + "=" + encodeURIComponent(params[k]))
                .join("&");
            window.open(this.$store.getters.apiUrl + "cierrecaja/exportCierreZ/" + tipo + "?" + query, "_blank");
        },

        /* ---------- Enlace a Análisis de negocio ---------- */

        // El informe de análisis está sujeto a la restricción 34 ("ventas por negocio"):
        // si el usuario la tiene, no se le ofrece el botón.
        comprobarAnalisisDisponible() {
            this.axios
                .get(this.$store.getters.apiUrl + "general/getRestrictionsByUser", {
                    params: { jwt: this.$store.getters.jwt },
                    headers: { Authorization: "Bearer " + this.$store.getters.jwt },
                })
                .then(response => {
                    const lista = response.data && response.data.response ? response.data.result || [] : [];
                    this.analisisDisponible = !lista.some(r => parseInt(r.idRestriction, 10) === 34);
                })
                .catch(() => { /* si falla, se deja el botón: la API vuelve a comprobar la restricción */ });
        },

        // Abre "Análisis de negocio" con la misma empresa y negocios (o sector) y el mes
        // de la fecha consultada; allí se lanza la consulta automáticamente.
        abrirAnalisis() {
            const query = {
                idCompany: String(this.idCompany),
                mes: moment(this.datefrom).format("YYYY-MM"),
            };
            if (this.modoSectores) {
                query.idSector = String(this.idSector);
            } else if (this.idsBusiness.length) {
                query.idsBusiness = this.idsBusiness.join(",");
            }
            this.$router.push({ name: "analisis-negocio", query });
        },

        /* ---------- Matriz ---------- */

        alternarBloque(clave) {
            this.$set(this.bloquesCerrados, clave, !this.bloquesCerrados[clave]);
            this.$nextTick(this.medirMatriz);
        },

        bloqueAbierto(clave) {
            return !this.bloquesCerrados[clave];
        },

        esNumerica(fila) {
            return ["importe", "entero", "decimal"].includes(fila.tipo);
        },

        esMedia(fila) {
            return FILAS_MEDIA.includes(fila.clave);
        },

        valorCelda(fila, c) {
            if (c.tipo === "z") {
                return Object.prototype.hasOwnProperty.call(fila.valores, c.clave) ? fila.valores[c.clave] : null;
            }
            // Subtotal del TPV: solo suma filas numéricas que no sean medias.
            if (!this.esNumerica(fila) || this.esMedia(fila)) return null;
            let suma = 0, hay = false;
            c.columnas.forEach(clave => {
                const v = fila.valores[clave];
                if (v !== null && v !== undefined) { suma += Number(v); hay = true; }
            });
            return hay ? suma : null;
        },

        /* Peso de la fila sobre su fila base (porcentajeDe) en esa columna: los
           tipos de pago sobre el total de formas de pago, cada medio sobre su tipo,
           efectivo/tarjeta/cheque del arqueo sobre la venta según TPV. Funciona
           igual para Z, subtotales de TPV y total, porque se calcula sobre los
           valores ya sumados. */
        filaBase(bloque, fila) {
            if (!fila.porcentajeDe) return null;
            return bloque.filas.find(f => f.clave === fila.porcentajeDe) || null;
        },

        porcentajeCelda(bloque, fila, c) {
            const base = this.filaBase(bloque, fila);
            if (!base) return null;
            return this.porcentaje(this.valorCelda(fila, c), this.valorCelda(base, c));
        },

        porcentajeTotal(bloque, fila) {
            const base = this.filaBase(bloque, fila);
            if (!base) return null;
            return this.porcentaje(this.totalFila(fila), this.totalFila(base));
        },

        porcentaje(valor, base) {
            if (valor === null || valor === undefined || base === null || base === undefined) return null;
            if (Math.abs(Number(base)) < 0.005) return null;
            return Number(valor) / Number(base) * 100;
        },

        formatearPorcentaje(pct) {
            return "(" + Number(pct).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " %)";
        },

        totalFila(fila) {
            if (!this.esNumerica(fila)) return null;
            if (!this.filtroVistaActivo) return fila.total;
            if (this.esMedia(fila)) return null;
            let suma = 0, hay = false;
            this.columnasZVista.forEach(c => {
                const v = fila.valores[c.clave];
                if (v !== null && v !== undefined) { suma += Number(v); hay = true; }
            });
            return hay ? suma : null;
        },

        filasVisibles(bloque) {
            if (!this.ocultarVacias) return bloque.filas;
            return bloque.filas.filter(fila => {
                if (fila.destacado || !this.esNumerica(fila)) return true;
                return this.columnasZVista.some(c => {
                    const v = fila.valores[c.clave];
                    return v !== null && v !== undefined && Math.abs(Number(v)) >= 0.005;
                });
            });
        },

        claseCelda(fila) {
            return this.esNumerica(fila) ? "text-right" : (fila.tipo === "hora" ? "text-center" : "");
        },

        esNegativo(v) {
            return typeof v === "number" && v < -0.005;
        },

        etiquetaFila(fila) {
            if (fila.etiquetaLiteral) {
                return fila.etiqueta === "medioDePago" ? fila.etiquetaLiteral : this.$t(fila.etiqueta) + " " + fila.etiquetaLiteral;
            }
            return this.$t(fila.etiqueta);
        },

        etiquetaSpecial(st) {
            if (st === 1) return this.$t("pendiente");
            if (st === 2) return this.$t("cuentaCasa");
            if (st === 3) return this.$t("valeRegalo");
            return "";
        },

        etiquetaTipoDocumento(tipo) {
            if (tipo === "R") return this.$t("rectificativa");
            if (tipo === "F") return this.$t("factura");
            return this.$t("ticket");
        },

        nombreColumna(clave) {
            const c = this.columnas.find(col => col.clave === clave);
            return c ? c.nombreTpv + " · " + this.etiquetaColumna(c) : clave;
        },

        fechaCaja(c) {
            return moment.unix(Number(c.apertura)).format("DD/MM/YYYY");
        },

        /* La caja se identifica por su fecha. Si el mismo TPV abrió varias Z el
           mismo día, se añade la hora de apertura para distinguirlas (sin nº de Z). */
        etiquetaColumna(c) {
            const fecha = this.fechaCaja(c);
            const repetidas = this.columnas.filter(o => o.idTpv === c.idTpv && this.fechaCaja(o) === fecha).length;
            return repetidas > 1 ? fecha + " " + moment.unix(Number(c.apertura)).format("HH:mm") : fecha;
        },

        /* Un aviso por línea en el tooltip (v-tooltip pinta HTML). */
        tooltipAvisos(c) {
            const escapar = t => String(t).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            return (c.avisos || [])
                .map(a => escapar(this.$i18n.t(a.codigo) + (a.detalle ? ": " + a.detalle : "")))
                .join("<br>");
        },

        unidadesVisibles(p) {
            let suma = 0;
            this.columnasZVista.forEach(c => { if (p.valores[c.clave]) suma += Number(p.valores[c.clave].unidades) || 0; });
            return suma;
        },

        importeVisible(p) {
            let suma = 0;
            this.columnasZVista.forEach(c => { if (p.valores[c.clave]) suma += Number(p.valores[c.clave].importe) || 0; });
            return suma;
        },

        /* ---------- Formato ---------- */

        formatear(valor, tipo) {
            if (valor === null || valor === undefined || valor === "") return "";
            switch (tipo) {
                case "importe":
                    return Number(valor).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
                case "decimal":
                    return Number(valor).toLocaleString("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                case "entero":
                    return String(Math.round(Number(valor)));
                case "hora":
                    return moment.unix(Number(valor)).format("DD/MM HH:mm");
                case "fecha":
                    return moment.unix(Number(valor)).format("DD/MM/YYYY");
                case "clave":
                    return this.$t(String(valor));
                default:
                    return String(valor);
            }
        },

        formatearUnidades(v) {
            return Number(v || 0).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
        },
    },
};
</script>

<style scoped>
.card {
    padding: 16px 20px;
    margin-bottom: 14px;
    border: 1px solid #e4e7ee;
    border-radius: 8px;
    background: #fff;
}

.cz-filtros label,
.cz-barra label {
    font-weight: 700;
    font-size: 0.78rem;
    color: #3c4166;
    margin-bottom: 4px;
}

/* Cabecera de filtros: una fila flex, cada filtro con su ancho mínimo; lo que no
   cabe salta de línea entero. Etiquetas y controles a altura fija para que nada
   quede escalonado (el tema pone márgenes a .btn y .form-group). */
.cz-filtros-fila {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px 18px;
}

.cz-filtro {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.cz-filtros label {
    display: block;
    height: 18px;
    line-height: 18px;
    white-space: nowrap;
    margin-bottom: 4px;
}

.cz-filtro-empresa {
    flex: 1 1 180px;
}

.cz-filtro-negocios {
    flex: 2 1 260px;
}

.cz-filtro-fecha {
    flex: 0 0 150px;
}

.cz-filtro-estado {
    flex: 0 0 160px;
}

.cz-filtro-accion {
    flex: 0 0 auto;
    margin-left: auto;
}

.cz-filtros .form-control,
.cz-filtro-accion .btn {
    height: 40px;
    margin: 0;
}

.cz-filtro-accion .btn {
    padding: 0 18px;
    line-height: 38px;
    white-space: nowrap;
}

.cz-select {
    width: 100%;
}

.cz-opciones {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 22px;
    margin-top: 14px;
}

@media (max-width: 767px) {
    .cz-filtro,
    .cz-filtro-accion {
        flex: 1 1 100%;
        margin-left: 0;
    }

    .cz-filtro-accion .btn {
        width: 100%;
    }
}

.cz-check {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500 !important;
    font-size: 0.84rem !important;
    color: #5b6183 !important;
    cursor: pointer;
}

.cz-check-deshabilitado {
    opacity: 0.6;
    cursor: not-allowed;
}

.cz-nota-sectores {
    margin-top: 10px;
    padding: 8px 12px;
    border-radius: 6px;
    background: #f3f7fc;
    border: 1px solid #d6e4f5;
    border-left: 4px solid #4a7dbf;
    color: #2f4a6d;
    font-size: 0.82rem;
}

.cz-nota-sectores i {
    margin-right: 6px;
}

/* Sin datos */
.cz-vacio {
    text-align: center;
    padding: 36px 20px 30px;
}

.cz-vacio-icono {
    width: 64px;
    height: 64px;
    margin: 0 auto 14px;
    border-radius: 50%;
    background: #eef0f7;
    color: #7a82a8;
    font-size: 1.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.cz-vacio h4 {
    margin: 0 0 6px;
    font-size: 1.05rem;
    font-weight: 700;
    color: #3c4166;
}

.cz-vacio p {
    margin: 0;
    color: #7a82a8;
    font-size: 0.88rem;
}

.cz-vacio-tpvs {
    margin-top: 18px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 6px;
}

.cz-vacio-etiqueta {
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7a82a8;
    margin-right: 6px;
}

.cz-chip-tpv {
    display: inline-block;
    background: #f4f5f9;
    border: 1px solid #e4e7ee;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 0.78rem;
    color: #3c4166;
}

/* Resumen de avisos (una línea; se despliega por tipo) */
.cz-resumen-avisos {
    margin-bottom: 14px;
    border: 1px solid #d6e4f5;
    border-left: 4px solid #4a7dbf;
    border-radius: 8px;
    background: #f3f7fc;
    color: #2f4a6d;
    font-size: 0.86rem;
}

.cz-resumen-avisos-alerta {
    border-color: #f5dfb6;
    border-left-color: #e0a12a;
    background: #fff9ee;
    color: #7a5a12;
}

.cz-resumen-avisos-cabecera {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    cursor: pointer;
    user-select: none;
}

.cz-resumen-avisos-texto {
    flex: 1;
}

.cz-resumen-avisos-accion {
    font-size: 0.78rem;
    font-weight: 600;
    white-space: nowrap;
    opacity: 0.85;
}

.cz-resumen-avisos-accion i {
    margin-left: 4px;
}

.cz-resumen-avisos-cuerpo {
    padding: 4px 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.cz-aviso-grupo {
    border-radius: 6px;
    padding: 8px 12px;
    background: #fff;
    border: 1px solid #e4e7ee;
    border-left: 4px solid #b0b4c6;
    color: #3c4166;
}

.cz-aviso-grupo-titulo {
    font-weight: 700;
    font-size: 0.84rem;
}

.cz-aviso-grupo-titulo i {
    margin-right: 6px;
}

.cz-aviso-grupo ul {
    margin: 6px 0 0;
    padding-left: 22px;
    font-size: 0.82rem;
    color: #5b6183;
}

.cz-aviso-grupo li {
    margin: 1px 0;
}

.cz-aviso-rojo {
    border-left-color: #d9534f;
    background: #fff5f5;
}

.cz-aviso-rojo .cz-aviso-grupo-titulo {
    color: #b3261e;
}

.cz-aviso-ambar {
    border-left-color: #e0a12a;
    background: #fffaf0;
}

.cz-aviso-ambar .cz-aviso-grupo-titulo {
    color: #8d6e00;
}

.cz-aviso-gris {
    border-left-color: #b0b4c6;
    background: #fafbfd;
}

.cz-contador {
    display: inline-block;
    min-width: 20px;
    text-align: center;
    border-radius: 10px;
    padding: 0 6px;
    margin-left: 6px;
    font-size: 0.72rem;
    background: #eef0f7;
    color: #3c4166;
}

/* Barra de vista (filtros locales + exportación) */
.cz-barra {
    padding: 10px 16px;
}

.cz-barra-fila {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px 22px;
}

.cz-barra-grupo {
    display: flex;
    flex-direction: column;
    min-width: 200px;
    flex: 1 1 200px;
}

/* La franja mide lo que su contenido: nunca se encoge por debajo y, si no cabe,
   salta de línea entera en vez de pisar a los botones de exportar. */
.cz-barra-grupo-franja {
    flex: 0 0 auto;
    min-width: 0;
}

.cz-barra label {
    display: block;
    line-height: 18px;
    height: 18px;
    white-space: nowrap;
}

.cz-select-barra {
    width: 100%;
}

.cz-ayuda {
    color: #7a82a8;
    margin-left: 4px;
    cursor: help;
    font-weight: 400;
}

.cz-franja {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 32px;
}

.cz-franja input,
.cz-franja .btn {
    height: 32px;
    margin: 0; /* el tema pone margin-bottom: 5px a todo .btn y descentraba la fila */
    padding-top: 0;
    padding-bottom: 0;
    line-height: 30px;
    font-size: 0.82rem;
}

.cz-franja input {
    flex: 0 0 auto;
    width: 118px;
}

.cz-quitar {
    color: #7a82a8;
    padding-left: 6px;
    padding-right: 6px;
}

.cz-quitar:hover {
    color: #c62828;
}

.cz-barra-exportar {
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    margin-left: auto;
}

.cz-botones-exportar {
    display: flex;
    gap: 8px;
    height: 32px;
}

.cz-btn-pdf,
.cz-btn-excel,
.cz-btn-analisis {
    color: #fff;
    border: none;
    font-weight: 600;
    height: 32px;
    margin: 0;
    padding: 0 14px;
    line-height: 32px;
}

@media (max-width: 767px) {
    .cz-barra-grupo,
    .cz-barra-grupo-franja,
    .cz-barra-exportar {
        flex: 1 1 100%;
        min-width: 100%;
        margin-left: 0;
    }

    .cz-franja {
        flex-wrap: wrap;
        height: auto;
    }

    .cz-botones-exportar .btn,
    .cz-botones-exportar .cz-btn-proximamente-wrap {
        flex: 1;
    }

    .cz-btn-proximamente-wrap .btn {
        width: 100%;
    }
}

.cz-btn-pdf i,
.cz-btn-excel i,
.cz-btn-analisis i {
    margin-right: 5px;
}

.cz-btn-pdf {
    background: #c9463d;
}

.cz-btn-pdf:hover {
    background: #a83a32;
    color: #fff;
}

.cz-btn-excel {
    background: #1f7a45;
}

.cz-btn-excel:hover {
    background: #196238;
    color: #fff;
}

/* Enlace al informe "Análisis de negocio" (mismo ámbito y mes de la fecha consultada) */
.cz-btn-analisis {
    background: #2f6db5;
    white-space: nowrap;
}

.cz-btn-analisis:hover {
    background: #26598f;
    color: #fff;
}

/* El tooltip va en el span: un botón disabled no dispara eventos de ratón */
.cz-btn-proximamente-wrap {
    display: inline-block;
    height: 32px;
}

.cz-btn-proximamente,
.cz-btn-proximamente:hover {
    background: #9aa5b1;
    cursor: not-allowed;
    opacity: 1;
    pointer-events: none;
}

/* Tablas anchas: desplazamiento horizontal propio */
.cz-scroll {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

/* Matriz */
.cz-matriz-card {
    padding: 0;
}

/* Flechas flotantes para desplazar la matriz: fijas a media altura de la
   pantalla, pegadas a los bordes de la tabla, solo mientras la matriz se ve. */
.cz-flecha {
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    z-index: 20;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid #c9cfe3;
    background: rgba(255, 255, 255, 0.94);
    color: #3b4a9c;
    box-shadow: 0 2px 8px rgba(16, 18, 85, 0.22);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    padding: 0;
}

.cz-flecha:hover {
    background: #eef2ff;
}

.cz-flecha:disabled {
    opacity: 0.3;
    cursor: default;
}

.cz-enlace-incidencias {
    color: #b3261e;
    font-weight: 600;
    text-decoration: none;
}

.cz-enlace-incidencias i {
    font-size: 0.66rem;
    margin-left: 2px;
}

.cz-enlace-incidencias:hover {
    text-decoration: underline;
}

.cz-detalle-incidencia {
    white-space: normal;
    min-width: 260px;
    color: #5b6183;
}

.cz-badge-inc-incidenciasPrecioModificado { background: #fff3e0; color: #b26a00; }
.cz-badge-inc-incidenciasLineaEliminada { background: #fdecea; color: #c62828; }
.cz-badge-inc-incidenciasCantidadModificada { background: #e3f2fd; color: #1565c0; }
.cz-badge-inc-incidenciasDescuentoModificado { background: #ede7f6; color: #5e35b1; }
.cz-badge-inc-incidenciasOtras { background: #eef0f7; color: #5b6183; }

.cz-matriz {
    margin: 0;
    font-size: 0.84rem;
}

.cz-matriz th,
.cz-matriz td {
    vertical-align: middle;
    white-space: nowrap;
}

.cz-concepto {
    min-width: 230px;
    position: sticky;
    left: 0;
    background: #fff;
    z-index: 1;
    box-shadow: 1px 0 0 #e4e7ee;
}

.cz-fila-negocio th {
    background: #3b4a9c;
    color: #fff;
    font-weight: 700;
    letter-spacing: 0.02em;
}

.cz-fila-negocio th.cz-concepto,
.cz-fila-tpv th.cz-concepto,
.cz-fila-z th.cz-concepto {
    background: #f6f7fb;
}

.cz-fila-tpv th {
    background: #e8ebf7;
    color: #2b3358;
    font-weight: 600;
}

.cz-fila-tpv small {
    font-weight: 400;
    color: #5b6183;
}

.cz-fila-z th {
    background: #f6f7fb;
    color: #3c4166;
    font-weight: 600;
}

.cz-z {
    font-weight: 700;
}

.cz-col-subtotal {
    background: #fafbff !important;
    font-style: italic;
}

.cz-subtotal-etiqueta {
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7a82a8;
}

.cz-col-abierta {
    background: #fff6e8 !important;
}

.cz-total {
    background: #eef0f7;
    font-weight: 700;
    min-width: 110px;
}

.cz-fila-bloque td {
    background: #e4e7f2;
    color: #2b3358;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.76rem;
    cursor: pointer;
    position: sticky;
    left: 0;
    border-top: 2px solid #c9cfe3;
}

.cz-fila-bloque td:hover {
    background: #d9ddef;
}

.cz-fila-bloque i {
    margin-right: 8px;
    width: 10px;
    color: #5b6183;
}

.cz-destacada td {
    font-weight: 700;
    background: #fbfbfe;
}

.cz-nivel-2 td.cz-concepto {
    padding-left: 28px;
    color: #5b6183;
}

.cz-nivel-3 td.cz-concepto {
    padding-left: 48px;
    color: #7a82a8;
    font-size: 0.8rem;
}

.cz-negativo {
    color: #c62828;
}

.cz-pct {
    display: inline-block;
    min-width: 70px;
    text-align: right;
    margin-left: 4px;
    font-size: 0.72rem;
    font-weight: 400;
    color: #7a82a8;
}

.cz-negativo .cz-pct {
    color: #c62828;
    opacity: 0.75;
}

.cz-badge {
    display: inline-block;
    border-radius: 4px;
    padding: 0 6px;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-left: 6px;
    vertical-align: middle;
}

.cz-badge-abierta {
    background: #f0a04b;
    color: #fff;
}

.cz-badge-especial {
    background: #eef2ff;
    color: #3b4a9c;
    border: 1px solid #d8ddf7;
}

.cz-badge-menu {
    background: #e8f5e9;
    color: #2e7d32;
}

.cz-badge-franja {
    background: #fff8e1;
    color: #8d6e00;
    border: 1px solid #ffe082;
}

.cz-icono-aviso {
    color: #e0a12a;
    margin-left: 6px;
    cursor: help;
}

/* Secciones bajo demanda */
.cz-seccion {
    padding: 0;
}

.cz-seccion-cabecera {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    cursor: pointer;
}

.cz-seccion-cabecera h5 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: #101255;
}

.cz-seccion-cabecera h5 i {
    margin-right: 8px;
    width: 10px;
}

.cz-seccion-cabecera small {
    margin-left: 8px;
    font-weight: 400;
}

.cz-seccion-cuerpo {
    padding: 0 20px 16px 20px;
}

.cz-buscador {
    max-width: 320px;
    margin-bottom: 10px;
}

.cz-tabla-productos,
.cz-tabla-tickets {
    font-size: 0.84rem;
    margin: 0;
}

.cz-tabla-productos td,
.cz-tabla-tickets td {
    vertical-align: middle;
}

.cz-ordenable {
    cursor: pointer;
    user-select: none;
}

.cz-tickets-filtros {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
}

.cz-tickets-filtros select {
    max-width: 220px;
}

.cz-buscador-doc {
    position: relative;
    flex: 1 1 260px;
    max-width: 380px;
}

.cz-buscador-doc input {
    padding-right: 30px;
}

.cz-limpiar {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    background: none;
    color: #7a82a8;
    cursor: pointer;
    padding: 2px 6px;
}

.cz-limpiar:hover {
    color: #c62828;
}

.cz-btn-desplegar {
    background: none;
    border: none;
    color: #5b6183;
    cursor: pointer;
}

.cz-fila-rectificativa td {
    background: #fff7f4;
}

.cz-fila-lineas td {
    background: #fbfbfe;
}

.cz-tabla-lineas {
    margin: 4px 0;
    font-size: 0.8rem;
}

.cz-linea-menu td {
    color: #7a82a8;
}

.cz-badge-doc {
    display: inline-block;
    min-width: 92px;
    white-space: nowrap;
    text-align: center;
    border-radius: 4px;
    padding: 1px 6px;
    margin-right: 6px;
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
}

.cz-badge-doc-T {
    background: #eef0f7;
    color: #5b6183;
}

.cz-badge-doc-F {
    background: #e3f2fd;
    color: #1565c0;
}

.cz-badge-doc-R {
    background: #fdecea;
    color: #c62828;
}

.cz-chip-pago {
    display: inline-block;
    background: #f4f5f9;
    border: 1px solid #e4e7ee;
    border-radius: 6px;
    padding: 1px 8px;
    margin: 1px 4px 1px 0;
    font-size: 0.78rem;
}

.cz-chip-especial {
    background: #eef2ff;
    border-color: #d8ddf7;
}

.cz-paginacion {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    margin-top: 12px;
}
</style>



// WEBPACK FOOTER //
// src/pages/Dashboard/Informes/Ventas/CierreCajaZ.vue